import React from "react";
import { Helmet } from "react-helmet-async";

export default function AtelierDuPendu() {
	const titlePage =
		"Atelier du Pendu | Site vitrine sellerie artisanale haut de gamme | Weblax Studio";
	const description =
		"Weblax a conçu le site vitrine de l'Atelier du Pendu, sellerie artisanale haut de gamme. Design minimaliste inspiré des codes du luxe, storytelling de marque et expérience utilisateur immersive pour sublimer un savoir-faire d'exception.";
	const url = "https://weblax.fr/realisations/atelier-du-pendu";

	const features = [
		{
			icon: "🎨",
			title: "Direction artistique premium",
			desc: "Identité visuelle pensée pour le luxe : typographie serif élégante, palette sobre et raffinée, espaces blancs généreux qui mettent le produit au centre.",
		},
		{
			icon: "📖",
			title: "Storytelling de marque",
			desc: "Architecture narrative centrée sur l'histoire de l'artisan, les gestes du métier et les matières nobles — pour créer un lien émotionnel avec le visiteur.",
		},
		{
			icon: "🖼️",
			title: "Galerie immersive",
			desc: "Présentation des créations en plein écran, avec transitions fluides et zooms haute définition pour valoriser chaque détail du travail artisanal.",
		},
		{
			icon: "📱",
			title: "Mobile haut de gamme",
			desc: "Expérience mobile aussi soignée que desktop — chaque animation, chaque proportion ont été pensées pour un rendu parfait sur tous les formats.",
		},
		{
			icon: "⚡",
			title: "Performance & fluidité",
			desc: "Chargement optimisé, animations légères et navigation sans friction pour que rien ne vienne perturber l'immersion dans l'univers de la marque.",
		},
		{
			icon: "🔍",
			title: "SEO de niche",
			desc: "Référencement ciblé sur les requêtes artisanat cuir, sellerie haut de gamme et fabrication sur mesure — pour capter une clientèle exigeante et qualifiée.",
		},
	];

	const steps = [
		{
			num: "01",
			title: "Immersion dans l'univers",
			desc: "Visite de l'atelier, rencontres avec l'artisan, analyse des créations et de la clientèle cible pour s'imprégner profondément de l'ADN de la marque.",
		},
		{
			num: "02",
			title: "Direction artistique",
			desc: "Définition de la charte graphique, sélection typographique, palette de couleurs et références visuelles cohérentes avec le positionnement luxe.",
		},
		{
			num: "03",
			title: "Architecture narrative",
			desc: "Structuration du contenu autour du storytelling : histoire, savoir-faire, matières, créations, contact — un parcours émotionnel pensé du début à la fin.",
		},
		{
			num: "04",
			title: "Développement & animations",
			desc: "Intégration React, animations au scroll, galerie immersive et optimisation des performances pour un rendu fluide et premium.",
		},
		{
			num: "05",
			title: "SEO & mise en ligne",
			desc: "Balisage sémantique, optimisation des métadonnées, schema.org artisan/LocalBusiness et déploiement sur infrastructure performante.",
		},
	];

	const results = [
		{
			icon: "⏱️",
			metric: "+85%",
			label: "Temps sur le site",
			color: "text-amber-600",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
		{
			icon: "💼",
			metric: "×3",
			label: "Demandes de devis",
			color: "text-stone-700",
			bg: "from-stone-50 to-neutral-50",
			border: "border-stone-200",
		},
		{
			icon: "🏆",
			metric: "Top 3",
			label: "SEO sellerie locale",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "📉",
			metric: "-45%",
			label: "Taux de rebond",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Atelier du Pendu — Site vitrine artisan sellier",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						about: {
							"@type": "LocalBusiness",
							name: "Atelier du Pendu",
							description:
								"Sellerie artisanale haut de gamme, fabrication d'articles en cuir sur mesure",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-stone-50 via-white to-amber-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 15% 60%, #d6cfc7 0%, transparent 50%), radial-gradient(circle at 85% 30%, #fef3c7 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">
							🪡 Sellerie artisanale
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">
							🎨 Site vitrine
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Quand le digital rencontre{" "}
						<span className="text-amber-700">l'artisanat d'exception</span> —
						Atelier du Pendu
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						L'Atelier du Pendu est une sellerie artisanale spécialisée dans la
						fabrication d'articles en cuir haut de gamme. Weblax a conçu un site
						vitrine sur mesure qui traduit, pixel par pixel, la précision du
						geste, la noblesse des matières et l'exigence d'un artisan
						passionné.
					</p>

					{/* Stats */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un artisan d'exception invisible sur le web
					</h2>
					<div className="grid md:grid-cols-2 gap-10 items-start">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								L'Atelier du Pendu réalise des pièces en cuir d'une précision
								remarquable — selles, harnais, accessoires équestres,
								maroquinerie sur mesure. Des créations qui prennent des heures,
								parfois des jours à façonner, et dont la valeur est
								immédiatement perceptible pour qui les tient entre les mains.
							</p>
							<p>
								Pourtant, sur le web, rien ne reflétait cet univers. Un site
								daté, des photos de mauvaise qualité, aucun storytelling : la
								perception digitale était aux antipodes de la réalité de
								l'atelier. Des clients potentiels passaient à côté d'une adresse
								d'exception faute d'une vitrine digitale à la hauteur.
							</p>
							<p>
								La mission de Weblax : créer un site vitrine qui soit lui-même
								une œuvre — sobre, précis, émotionnel — capable de transmettre
								en quelques secondes la valeur de ce savoir-faire unique.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Avant la refonte
							</div>
							{[
								{
									label: "Image de marque digitale",
									value: "Inexistante",
									color: "text-red-500",
								},
								{
									label: "Qualité perçue en ligne",
									value: "Non représentative",
									color: "text-red-500",
								},
								{
									label: "Référencement sellerie",
									value: "Hors top 50",
									color: "text-orange-500",
								},
								{
									label: "Demandes de contact web",
									value: "Quasi nulles",
									color: "text-orange-500",
								},
								{
									label: "Expérience mobile",
									value: "Inexistante",
									color: "text-red-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Brief client ── */}
				<section className="bg-gradient-to-r from-stone-50 to-amber-50 border border-stone-200 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Brief client
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que l'Atelier du Pendu attendait
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🪡",
								text: "Un site qui reflète la précision et la noblesse du travail artisanal",
							},
							{
								icon: "📸",
								text: "Une mise en valeur photographique des créations et des matières",
							},
							{
								icon: "✍️",
								text: "Un storytelling fort autour de l'histoire et du geste de l'artisan",
							},
							{
								icon: "📱",
								text: "Une expérience mobile soignée, aussi premium que desktop",
							},
							{
								icon: "🎯",
								text: "Une meilleure visibilité sur les recherches sellerie artisanale",
							},
							{
								icon: "📬",
								text: "Générer davantage de demandes de contact et de devis qualifiés",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-stone-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Notre approche ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Philosophie
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Moins, mais mieux — l'esthétique du luxe
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Dans l'univers du haut de gamme et de l'artisanat, le design doit
						s'effacer pour laisser place à l'essentiel : le produit et son
						créateur. Notre approche a consisté à épurer chaque élément jusqu'à
						ne garder que ce qui renforce la perception de valeur. Chaque choix
						typographique, chaque espace blanc, chaque transition est
						intentionnel.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-amber-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Processus ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Processus
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						Notre méthode, étape par étape
					</h2>
					<div className="space-y-0">
						{steps.map((step, i) => (
							<div key={step.num} className="flex gap-8 group">
								<div className="flex flex-col items-center">
									<div className="w-14 h-14 rounded-full border-2 border-amber-200 bg-amber-50 flex items-center justify-center text-amber-700 font-bold text-sm shrink-0 group-hover:border-amber-400 transition-colors">
										{step.num}
									</div>
									{i < steps.length - 1 && (
										<div className="w-px flex-1 bg-amber-100 my-2" />
									)}
								</div>
								<div className="pb-10 pt-3 flex-1">
									<h3 className="font-semibold text-foreground mb-2">
										{step.title}
									</h3>
									<p className="text-sm text-muted-foreground leading-relaxed">
										{step.desc}
									</p>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Technologies choisies pour ce projet
					</h2>
					<div className="flex flex-wrap gap-3">
						{[
							{ name: "React", desc: "Interface utilisateur" },
							{ name: "Next.js", desc: "SSR & SEO" },
							{ name: "Tailwind CSS", desc: "Design system" },
							{ name: "Framer Motion", desc: "Animations premium" },
							{ name: "Schema.org", desc: "LocalBusiness & Artisan" },
							{ name: "Cloudinary", desc: "Optimisation images" },
							{ name: "Vercel", desc: "Déploiement edge" },
						].map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Des résultats concrets, mesurables
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Un site vitrine bien conçu n'est pas qu'une carte de visite — c'est
						un outil commercial. Les résultats obtenus après le lancement
						démontrent qu'un investissement dans la qualité digitale génère un
						retour tangible pour un artisan haut de gamme.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+85%",
								detail: "de temps passé sur le site",
								desc: "Les visiteurs explorent davantage, s'imprègnent de l'univers et passent plus de temps sur les pages créations et savoir-faire.",
								color: "text-amber-700",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
							{
								metric: "×3",
								detail: "de demandes de devis reçues",
								desc: "Le nouveau site génère trois fois plus de prises de contact qualifiées, avec des prospects mieux informés et mieux alignés avec le positionnement premium.",
								color: "text-stone-700",
								bg: "from-stone-50 to-neutral-50",
								border: "border-stone-200",
							},
							{
								metric: "Top 3",
								detail: "sur les requêtes SEO ciblées",
								desc: "Positionnement Google atteint sur les requêtes sellerie artisanale, articles cuir sur mesure et sellier haut de gamme dans la zone de chalandise.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "-45%",
								detail: "de taux de rebond",
								desc: "Moins de 30% de rebond grâce à une entrée dans le site plus qualitative et une expérience utilisateur qui donne envie de rester et d'explorer.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-amber-100 bg-gradient-to-br from-stone-50 to-amber-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🪡</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"Pour la première fois, quand je donne mon site à un client
						potentiel, il dit exactement ce que je ressens pour mon métier.
						Weblax a compris quelque chose que j'avais du mal à expliquer
						moi-même."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Artisan fondateur — Atelier du Pendu
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-amber-500" />
						<span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Le design minimaliste comme stratégie de marque
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							L'Atelier du Pendu démontre que pour un artisan haut de gamme, le
							site vitrine n'est pas un accessoire — c'est le premier point de
							contact avec une clientèle exigeante qui juge la qualité d'un
							produit à travers la qualité de sa présentation digitale.
						</p>
						<p>
							En adoptant une approche minimaliste inspirée des codes du luxe,
							Weblax a créé une plateforme qui ne dit pas tout, mais dit
							l'essentiel — avec la précision que l'on attend d'un artisan.
						</p>
						<p>
							Ce projet illustre notre conviction : le meilleur design est celui
							qu'on ne remarque pas, mais qui laisse une impression indélébile.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre artisanat mérite une vitrine d'exception
							</h3>
							<p className="text-sm opacity-70">
								Site vitrine, refonte, identité digitale — parlons de votre
								projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-amber-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
